/** Format detection + pretty-printing for stored documents (import inputs,
 *  webhook bodies, upstream responses).
 *
 * Detection is deliberately client-side and takes precedence over whatever
 * `format` the backend reports: the console endpoints sniff only the first
 * character and label anything that isn't `{`/`[` as "xml" — plain text
 * included. Here a document is XML only if it actually starts with `<`.
 *
 * `prettyPrint` returns `null` (rather than throwing or silently handing back
 * the input) when a document can't be reformatted, so callers can tell "no
 * change needed" apart from "this isn't well-formed" and say so in the UI.
 * Malformed payloads are routine in this data, so that path is normal, not
 * exceptional.
 */
import xmlFormat from 'xml-formatter';

export type DocFormat = 'xml' | 'json' | 'text';

/** Documents larger than this aren't pretty-printed unless asked: both
 *  formatters are synchronous and would block the UI thread. */
export const PRETTY_AUTO_MAX = 2_000_000;

/** Leading byte-order mark + whitespace, which hide the first real character. */
const LEADING = /^[\uFEFF\s]+/;

/** Sniff the document format.
 *
 * `hint` (the server's `format`, or a content type) is only ever consulted for
 * JSON — a JSON document can legitimately be a bare scalar (`42`, `"x"`),
 * whereas an XML document always starts with `<`, so an "xml" hint over
 * content that doesn't is just the endpoint's default talking. */
export function detectFormat(text: string, hint?: string): DocFormat {
  const head = text.replace(LEADING, '').charAt(0);
  if (head === '<') return 'xml';
  if (head === '{' || head === '[') return 'json';
  if (head !== '' && hint === 'json') return 'json';
  return 'text';
}

/** Indent a document. Returns null when it can't be formatted (not
 *  well-formed, or a format with no canonical indentation). */
export function prettyPrint(text: string, fmt: DocFormat): string | null {
  if (!text.trim()) return null;
  if (fmt === 'xml') {
    try {
      return xmlFormat(text, {
        indentation: '  ',
        // Keep `<tag>value</tag>` on one line — what makes attribute-heavy
        // OTA feeds readable instead of three lines per leaf.
        collapseContent: true,
        // The library defaults to '\r\n'.
        lineSeparator: '\n',
      });
    } catch {
      return null;
    }
  }
  if (fmt === 'json') {
    try {
      return JSON.stringify(JSON.parse(text), null, 2);
    } catch {
      return null;
    }
  }
  return null;
}
